<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {storeToRefs} from "pinia";
import {ChevronLeft} from "@lucide/vue";
import {
    NotificationSequence,
    NotificationSequenceDetails
} from "@/notification-sequences/interfaces/NotificationSequence";
import FlowchartNotificationSequenceActivation
    from "@/flowcharts/Components/flowchart-modal/FlowchartNotificationSequenceActivation.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {Button} from "@/core/components/ui/button";
import {notificationSequenceApi} from "@/container";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import {usePatientStore} from "@/stores/patient.store";

const props = defineProps<{
    sequence: NotificationSequence;
    /** The patient the flowchart is run for. */
    consumer: Customer | Beneficiary;
}>();

const emits = defineEmits<{
    (e: 'back'): void;
    (e: 'activated'): void;
}>();

const details = ref<NotificationSequenceDetails | null>(null);
const loading = ref<boolean>(false);
const patientStore = usePatientStore();

/** The insured customer: the sequence is always sent to them, even for one of their beneficiaries. */
const {customer} = storeToRefs(patientStore);

/** The beneficiary the flowchart is run for, if it is not the insured customer. */
const beneficiary = computed((): Beneficiary | undefined => {
    return props.consumer.consumer_key !== customer.value?.consumer_key
        ? props.consumer as Beneficiary
        : undefined;
});

const handleCertified = async () => {
    return await patientStore.refreshCustomer();
};

onMounted(async () => {
    loading.value = true;

    try {
        details.value = await notificationSequenceApi().read(props.sequence.id);
    } finally {
        loading.value = false;
    }
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center gap-2">
      <Button variant="ghost" size="icon" @click="$emit('back')">
        <ChevronLeft />
      </Button>
      <h3>
        {{ sequence.name }}
      </h3>
    </div>
    <BaseFlexSpinner v-if="loading" />
    <FlowchartNotificationSequenceActivation
      v-else-if="details && customer"
      :sequence="details"
      :customer="customer"
      :consumer-key="customer.consumer_key"
      :beneficiary="beneficiary"
      @started="emits('activated')"
      @certified="handleCertified"
    />
  </div>
</template>
